/**
 * Stamp SED BTM-grounding sections from site Project Intent (v1.2.1 HR-5).
 *
 * Mirrors `stampTechnologyFromIntent` — these are INTENT-stamped sections
 * (`status: 'verified-from-intent'`), not live-provider sections. They make
 * the BTM model (cooling axes, power semantics, firm-capacity / ELCC) into
 * first-class VERIFIED evidence rather than facts that only leaked through the
 * PROJECT INTENT prose block.
 *
 *   P2-2 — `cooling`      : 3 orthogonal cooling axes + resolved PUE +
 *                           water-by-sink estimate + waste-heat credit.
 *   P2-3 — `power`        : basis-explicit MW (IT / facility / MVA).
 *   P2-3 — `firmCapacity` : ELCC / firm-capacity accreditation intent.
 *
 * `lcoe` (P2-3) and `fuelSupply` (P2-4) are stamped separately because they
 * read financialAssumptions + the natural-gas provider (see `groundGasFuelCost`
 * + the fuelSupply compile path), not just the raw intent.
 *
 * MIGRATION-SAFE: every section is ADDITIVE + optional. When no intent (or no
 * relevant axis) is declared the section is written `unavailable` honestly;
 * legacy SEDs without these fields load unchanged. User-doc / context / report /
 * research / dam slots are NEVER overwritten (PROTECTED_STATUSES).
 */
import type { ProjectIntent } from '../types/siteRag';
import { projectCoolingGeneration, resolveCoolingPair } from '../types/siteRag';
import type { BopHeatRejection } from '../types/siteRag';
import {
  explicitUnknownIntentFieldLabel,
  explicitUnknownIntentRecommendation,
  isIntentFieldExplicitlyUnknown,
} from './intent/explicitUnknown';
import {
  resolveIntentPowerStack,
  resolveIntentPowerStackBand,
  resolvePueWithSource,
  isValidPue,
  isValidPowerFactor,
  type PowerBandLayer,
  type PowerBasis,
  type PueSource,
} from './powerStack';
import type {
  SiteEvidenceDocument,
  CoolingEvidence,
  PowerSemanticsEvidence,
  FirmCapacityEvidence,
  EvidenceSection,
} from '../types/siteEvidence';

const PROTECTED_STATUSES = new Set([
  'verified-from-doc',
  'verified-from-context',
  'verified-from-report',
  'verified-from-research',
  'verified-from-dam',
]);

/**
 * Water-withdrawal ESTIMATE by cold-end SINK class, litres per MWh of REJECTED
 * THERMAL energy. Sink-class planning estimates only — a real value needs
 * cooling-tower sizing (out of scope; flagged). Order-of-magnitude basis:
 *   - dry-cooler / free-cooling : ~0 (air-side; negligible water).
 *   - wet-cooling-tower         : high evaporative make-up.
 *   - hybrid                    : ~mid (part-time wet operation).
 *   - once-through              : large WITHDRAWAL (mostly returned — not consumed).
 *   - absorption-cchp           : low (waste-heat-driven; minimal cooling water).
 *   - unknown                   : null (no estimate; do not fabricate).
 */
export const WATER_WITHDRAWAL_L_PER_MWHTH: Record<BopHeatRejection, number | null> = {
  'dry-cooler': 0,
  'free-cooling': 0,
  'wet-cooling-tower': 1700,
  hybrid: 850,
  'once-through': 120000,
  'absorption-cchp': 200,
  unknown: null,
};

function mapPueSourceToBasis(source: PueSource): CoolingEvidence['pueBasis'] {
  switch (source) {
    case 'user':
    case 'design-target':
    case 'simulation':
      return 'declared';
    case 'config-estimate':
      return 'config-derived';
    case 'default':
      return 'base-estimate';
    default:
      return 'unavailable';
  }
}

/**
 * Build an honest `unavailable` section.
 *
 * SED D3.5 — `intentField` names the Project Intent field that, when
 * declared, would populate this section. When supplied, the section is
 * stamped `unavailableCause: 'intent-missing'` + a human-readable
 * `emptyReason`, so a silently-empty intent-stamped section is no longer
 * indistinguishable from a failed provider. Additive + optional: callers
 * that omit it get the historic shape (no `unavailableCause`).
 */
function emptySection<T>(source: string, intentField?: string): EvidenceSection<T> {
  return {
    status: 'unavailable',
    data: null,
    source,
    confidence: 0,
    retrievedAt: new Date().toISOString(),
    anomalies: [],
    ...(intentField
      ? {
          unavailableCause: 'intent-missing' as const,
          emptyReason: `Not yet declared — complete Project Intent → ${intentField} to populate this section.`,
        }
      : {}),
  };
}

// ── User-recorded unknowns ───────────────────────────────────────────────────
//
// Recording a Project Intent field as UNKNOWN is the user's answer, not a
// missing input. An intent-stamped section left empty BECAUSE its source field
// was recorded unknown must therefore (a) never read as verified or fresh, and
// (b) never carry the "complete Project Intent → X" ask that `emptySection`
// attaches to a field nobody has answered. It carries the recorded unknown and
// its follow-up instead.
//
// Existing SED vocabulary only — no new status, no new `unavailableCause`:
//   - `status: 'unavailable'`, `data: null`     → never verified, never fresh;
//   - `qualification: 'unknown'`                → the typed "unknown" marker;
//   - `provenanceDetail.mergeReason`            → stable code naming the field;
//   - `emptyReason`                             → the unknown + its follow-up.
// `unavailableCause` is deliberately left unset: 'intent-missing' means "not yet
// declared" and is what raises the D3.5 "complete Project Intent" gap.

/** `provenanceDetail.mergeReason` prefix of a recorded-unknown section. */
export const RECORDED_UNKNOWN_MERGE_REASON_PREFIX = 'project-intent:recorded-unknown:';

/** "<Label> was recorded as UNKNOWN by the user … Open follow-up: …" */
export function recordedUnknownIntentSentence(field: string): string {
  return `${explicitUnknownIntentFieldLabel(field)} was recorded as UNKNOWN by the user in Project Intent — `
    + `this is the user's answer, not a missing input. Open follow-up: ${explicitUnknownIntentRecommendation(field)}`;
}

/** An `unavailable` intent-stamped section for a user-recorded unknown field. */
export function recordedUnknownSection<T>(source: string, field: string): EvidenceSection<T> {
  return {
    status: 'unavailable',
    data: null,
    source,
    confidence: 0,
    retrievedAt: new Date().toISOString(),
    anomalies: [],
    qualification: 'unknown',
    emptyReason: recordedUnknownIntentSentence(field),
    provenanceDetail: { mergeReason: `${RECORDED_UNKNOWN_MERGE_REASON_PREFIX}${field}` },
  };
}

/**
 * The Project Intent field an intent-stamped section was left empty for as a
 * user-recorded unknown, or null for every other section (verified, provider,
 * "not yet declared", legacy).
 */
export function recordedUnknownFieldOfSection(
  sec: Pick<EvidenceSection<unknown>, 'status' | 'qualification' | 'provenanceDetail'> | null | undefined,
): string | null {
  if (!sec || sec.status !== 'unavailable' || sec.qualification !== 'unknown') return null;
  const reason = sec.provenanceDetail?.mergeReason;
  if (typeof reason !== 'string' || !reason.startsWith(RECORDED_UNKNOWN_MERGE_REASON_PREFIX)) return null;
  const field = reason.slice(RECORDED_UNKNOWN_MERGE_REASON_PREFIX.length);
  return field.length > 0 ? field : null;
}

/**
 * S1 — the PUE the intent stamps would otherwise be a PLATFORM figure (config
 * estimate / default) standing in for a PUE the user recorded as unknown or a
 * physically invalid legacy stored value (`pue < 1`). A valid declared design
 * target is the user's own figure and is kept. The explicit marker or an
 * invalid declared value wins over a fallback estimate.
 */
export function isPueRecordedUnknown(
  intent: ProjectIntent | null | undefined,
  pueSource: PueSource,
): boolean {
  const hasInvalidPueValue = intent?.pueValue !== undefined && !isValidPue(intent.pueValue);
  const hasInvalidPueTarget = intent?.pueTarget !== undefined && !isValidPue(intent.pueTarget);
  if (
    (hasInvalidPueValue || hasInvalidPueTarget) &&
    pueSource !== 'user' &&
    pueSource !== 'design-target' &&
    pueSource !== 'simulation'
  ) {
    return true;
  }
  return isIntentFieldExplicitlyUnknown(intent, 'pueValue') && pueSource !== 'design-target';
}

/**
 * S1 — the power factor was recorded unknown or a legacy stored value is
 * physically invalid (`<= 0` or `> 1`); do not stamp the default PF (`0.95`).
 */
export function isPowerFactorRecordedUnknown(intent: ProjectIntent | null | undefined): boolean {
  if (intent?.powerFactor !== undefined && !isValidPowerFactor(intent.powerFactor)) return true;
  return isIntentFieldExplicitlyUnknown(intent, 'powerFactor');
}

/**
 * Which derived power layers depend on a recorded-unknown PUE / power factor.
 * facility basis: IT = entered ÷ PUE; MVA = facility ÷ PF.
 * it-load basis:  facility = entered × PUE; MVA = facility ÷ PF; generation
 *                 required scales the facility load.
 */
export function unknownDependentLayers(
  basis: PowerBasis,
  pueUnknown: boolean,
  pfUnknown: boolean,
): { itLoad: boolean; facility: boolean; apparent: boolean; generation: boolean } {
  return {
    itLoad: pueUnknown && basis === 'facility',
    facility: pueUnknown && basis === 'it-load',
    apparent: pfUnknown || (pueUnknown && basis === 'it-load'),
    generation: pueUnknown && basis === 'it-load',
  };
}

function intentStampedSection<T>(data: T, source: string, confidence: number, mergeReason: string): EvidenceSection<T> {
  return {
    status: 'verified-from-intent',
    data,
    source,
    confidence,
    retrievedAt: new Date().toISOString(),
    anomalies: [],
    provenanceDetail: { mergeReason },
  };
}

/**
 * Q2 — the section to write when the intent yields no data, or `undefined` to
 * leave the existing slot as it is.
 *
 *  - Source field recorded UNKNOWN → a recorded-unknown section, replacing an
 *    absent, intent-stamped or empty slot (so an earlier "not yet declared"
 *    section does not keep asking after the user answered "unknown").
 *  - Otherwise → today's `missing()` section under today's condition (absent or
 *    previously intent-stamped), plus the one new transition: a recorded-unknown
 *    section whose marker has since been cleared returns to "not yet declared".
 */
function emptyOrRecordedUnknownSection<T>(
  existing: EvidenceSection<T> | undefined,
  recordedUnknownField: string | null,
  recordedUnknownSource: string,
  missing: () => EvidenceSection<T>,
): EvidenceSection<T> | undefined {
  if (recordedUnknownField) {
    if (!existing || existing.status === 'verified-from-intent' || existing.status === 'unavailable') {
      return recordedUnknownSection<T>(recordedUnknownSource, recordedUnknownField);
    }
    return undefined;
  }
  if (!existing || existing.status === 'verified-from-intent' || recordedUnknownFieldOfSection(existing) != null) {
    return missing();
  }
  return undefined;
}

/** True when a slot's existing status must not be overwritten by an intent stamp. */
function isProtected(sec: EvidenceSection<unknown> | undefined): boolean {
  return !!sec?.status && PROTECTED_STATUSES.has(sec.status);
}

// ── P2-2: cooling ───────────────────────────────────────────────────────────

export function buildCoolingEvidenceFromIntent(
  intent: ProjectIntent | null | undefined,
): CoolingEvidence | null {
  if (!intent) return null;

  // The 3 orthogonal axes (resolved through the canonical helpers so the pair
  // is never contradictory — same boundary the prompts use).
  const coolingStrategy = intent.coolingStrategy ?? 'undecided';
  const pair = resolveCoolingPair(intent);
  const coolingGeneration = projectCoolingGeneration(intent) ?? pair.coolingGeneration;
  const bopHeatRejection = pair.bopHeatRejection;

  // A cooling section is only meaningful when SOME axis was declared. An intent
  // with everything undecided / absent yields null (section stays unavailable —
  // do not penalise completeness for a site that hasn't chosen cooling yet).
  // Recording bopHeatRejection as unknown is an explicit user declaration.
  const anyAxisDeclared =
    (coolingStrategy && coolingStrategy !== 'undecided') ||
    (bopHeatRejection != null && bopHeatRejection !== 'unknown') ||
    (coolingGeneration != null && coolingGeneration !== 'unknown' && coolingGeneration !== 'none') ||
    bopHeatRejection === 'unknown' ||
    isIntentFieldExplicitlyUnknown(intent, 'bopHeatRejection');
  if (!anyAxisDeclared) return null;

  // Resolved PUE + provenance. S1 — a PUE the user recorded as unknown is not
  // replaced by the platform's config estimate here: the section carries it as
  // unknown (`resolvedPue: null`, `pueBasis: 'unavailable'` — the type's own
  // "null when unknown" vocabulary). Downstream models still run their own
  // labelled estimate; the SED just never asserts one as this site's PUE.
  const { pue, source: pueSource } = resolvePueWithSource(intent);
  const pueUnknown = isPueRecordedUnknown(intent, pueSource);
  const pueBasis = pueUnknown ? 'unavailable' : mapPueSourceToBasis(pueSource);

  // Water-withdrawal estimate keyed off the cold-end SINK.
  const waterWithdrawal =
    bopHeatRejection != null
      ? {
          litersPerMwhThermal: WATER_WITHDRAWAL_L_PER_MWHTH[bopHeatRejection] ?? null,
          basis:
            bopHeatRejection === 'unknown'
              ? 'Sink recorded as unknown. Recommendation: Shortlist dry, hybrid, and wet rejection against local design conditions, then obtain a vendor capacity check.'
              : 'Sink-class estimate (planning order-of-magnitude). A site-specific value requires cooling-tower sizing.',
          sink: bopHeatRejection,
        }
      : null;

  // Waste-heat credit — only claimable when the cooling-generation machine is
  // thermal AND a waste-heat offtake is declared.
  const thermalGen =
    coolingGeneration === 'absorption-thermal' || coolingGeneration === 'steam-driven-mechanical';
  const offtake = intent.wasteHeatOfftake;
  const offtakeDeclared = offtake != null && offtake.status !== 'none';
  const wasteHeatCredit = thermalGen
    ? {
        eligible: thermalGen && offtakeDeclared,
        heatUsableFraction: offtake?.heatUsableFraction ?? null,
        thermalLoadMwth: offtake?.thermalLoadMwth ?? null,
        offtakeStatus: offtake?.status ?? 'undeclared',
      }
    : null;

  return {
    coolingStrategy,
    bopHeatRejection,
    coolingGeneration,
    resolvedPue: !pueUnknown && Number.isFinite(pue) ? pue : null,
    pueBasis,
    waterWithdrawal,
    wasteHeatCredit,
  };
}

// ── P2-3: power semantics ────────────────────────────────────────────────────

export function buildPowerSemanticsEvidenceFromIntent(
  intent: ProjectIntent | null | undefined,
): PowerSemanticsEvidence | null {
  if (!intent) return null;
  const stack = resolveIntentPowerStack(intent);
  // S1 — PUE / power factor recorded as UNKNOWN by the user. The stack still
  // resolves (downstream models run a labelled estimate), but the SED must not
  // carry the platform's estimate — nor any layer derived through it — as this
  // site's intent-stamped figure. Such values are written null (the type's
  // unknown). Merely UNSET fields keep today's estimate exactly.
  const pfUnknown = isPowerFactorRecordedUnknown(intent);
  const basisUnknown = isIntentFieldExplicitlyUnknown(intent, 'powerBasis');

  if (stack.enteredMW == null) {
    // No exact MW. A RANGE BUCKET is still a real, citable signal — resolve it
    // as a band rather than dropping the section entirely. Only a genuinely
    // undecided intent yields nothing.
    const band = resolveIntentPowerStackBand(intent);
    if (!band) return null; // undecided ⇒ still no power section (don't fabricate one)

    const pueUnknown = isPueRecordedUnknown(intent, band.pueSource);
    const dependent = basisUnknown
      ? { itLoad: true, facility: true, apparent: true, generation: true }
      : unknownDependentLayers(band.basis, pueUnknown, pfUnknown);
    const edges = (layer: PowerBandLayer) => ({ low: layer.low, high: layer.high });
    // A layer derived through a recorded unknown is not derivable: both edges
    // null. (`openLow` / `openHigh` still describe the ENTERED band only.)
    const notDerivable = { low: null, high: null };
    return {
      powerBasis: basisUnknown ? null : band.basis,
      // GUARD — every scalar stays null in bucket mode. These fields back
      // TIER-1 governed facts; a bucket-derived range must never resolve as a
      // verified point value. The band rides in `bucketBand` instead.
      declaredMw: null,
      pueValue: !pueUnknown && Number.isFinite(band.pue) ? band.pue : null,
      powerFactor: !pfUnknown && Number.isFinite(band.powerFactor) ? band.powerFactor : null,
      itLoadMw: null,
      facilityMw: null,
      apparentMva: null,
      basisProvenance: basisUnknown ? 'user-unknown' : intent.powerBasis != null ? 'declared' : 'default-applied',
      figureBasis: 'bucket-derived',
      bucketBand: {
        bucket: band.bucket,
        bucketLabel: band.bucketLabel,
        openLow: band.openLow,
        openHigh: band.openHigh,
        sizingBasisMw: band.sizingBasisMW,
        sizingBasisRule: band.sizingBasisRule,
        enteredMw: edges(band.enteredMW),
        itLoadMw: dependent.itLoad ? notDerivable : edges(band.itLoadMW),
        facilityMw: dependent.facility ? notDerivable : edges(band.facilityMW),
        apparentMva: dependent.apparent ? notDerivable : edges(band.apparentMVA),
        generationRequiredMw: dependent.generation ? notDerivable : edges(band.generationRequiredMW),
      },
    };
  }

  const pueUnknown = isPueRecordedUnknown(intent, stack.pueSource);
  const dependent = basisUnknown
    ? { itLoad: true, facility: true, apparent: true, generation: true }
    : unknownDependentLayers(stack.basis, pueUnknown, pfUnknown);
  return {
    powerBasis: basisUnknown ? null : stack.basis,
    declaredMw: stack.enteredMW,
    pueValue: !pueUnknown && Number.isFinite(stack.pue) ? stack.pue : null,
    powerFactor: !pfUnknown && Number.isFinite(stack.powerFactor) ? stack.powerFactor : null,
    itLoadMw: dependent.itLoad ? null : stack.itLoadMW,
    facilityMw: dependent.facility ? null : stack.facilityMW,
    apparentMva: dependent.apparent ? null : stack.apparentMVA,
    basisProvenance: basisUnknown ? 'user-unknown' : intent.powerBasis != null ? 'declared' : 'default-applied',
    figureBasis: 'exact',
    bucketBand: null,
  };
}

// ── P2-3: firm-capacity (ELCC) ───────────────────────────────────────────────

export function buildFirmCapacityEvidenceFromIntent(
  intent: ProjectIntent | null | undefined,
  isoRegion?: string | null,
): FirmCapacityEvidence | null {
  if (!intent) return null;
  const cc = intent.capacityCreditIntent;
  // No ELCC intent declared ⇒ section unavailable (not a fabricated 'none').
  if (!cc) return null;

  return {
    accreditationTarget: cc.target,
    targetMw: cc.targetMW ?? null,
    accreditationNote: cc.accreditationNote ?? null,
    isoRegion: isoRegion ?? null,
  };
}

// ── Stamp orchestrator ───────────────────────────────────────────────────────

/**
 * Write or refresh the SED `cooling` / `power` / `firmCapacity` sections from
 * project intent. Does not overwrite doc/context/report/research/dam slots.
 * Returns true when any section was written.
 */
export function stampBtmGroundingFromIntent(
  sed: SiteEvidenceDocument,
  intent: ProjectIntent | null | undefined,
): boolean {
  let wrote = false;

  // ── cooling (P2-2) ──
  if (!isProtected(sed.cooling)) {
    const data = buildCoolingEvidenceFromIntent(intent);
    if (data) {
      sed.cooling = intentStampedSection(
        data,
        'Project Intent — BTM cooling configuration (3 axes)',
        0.85,
        'project-intent:cooling',
      );
      // SED D3.2 — the sink-class water-withdrawal is a CLASS-ESTIMATE, not a
      // measured value; attach an explicit ±40% band so the value-level
      // uncertainty is honest (a real figure needs cooling-tower sizing). Only
      // for a positive, finite estimate — a 0 (dry/free-cooling) or null
      // (unknown sink) carries no band (never fabricate uncertainty).
      const wl = data.waterWithdrawal?.litersPerMwhThermal;
      if (typeof wl === 'number' && Number.isFinite(wl) && wl > 0) {
        sed.cooling.valueUncertainty = [
          {
            field: 'waterWithdrawal.litersPerMwhThermal',
            pointEstimate: wl,
            low: Math.round(wl * 0.6),
            high: Math.round(wl * 1.4),
            ciBasis: 'class-estimate',
            relativeWidthPct: 80,
          },
        ];
      }
      wrote = true;
    } else {
      // Q2 — no cooling axis declared. A rack-side strategy the user recorded
      // as unknown is an answer: carry it with its follow-up, not the
      // "complete Project Intent → Cooling" ask.
      const next = emptyOrRecordedUnknownSection<CoolingEvidence>(
        sed.cooling,
        isIntentFieldExplicitlyUnknown(intent, 'coolingStrategy') ? 'coolingStrategy' : null,
        'Project Intent — IT cooling strategy recorded as unknown by the user (no cooling axis declared)',
        () => emptySection<CoolingEvidence>('Project Intent — cooling configuration not yet declared', 'Cooling'),
      );
      if (next) {
        sed.cooling = next;
        wrote = wrote || sed.cooling.status === 'unavailable';
      }
    }
  }

  // ── power semantics (P2-3) ──
  if (!isProtected(sed.power)) {
    const data = buildPowerSemanticsEvidenceFromIntent(intent);
    if (data) {
      const isBand = data.figureBasis === 'bucket-derived';
      sed.power = intentStampedSection(
        data,
        isBand
          ? 'Project Intent — power semantics (bucket-derived band, no exact MW declared)'
          : 'Project Intent — power semantics (basis-explicit MW)',
        // A range bucket is a weaker signal than a declared figure: the operator
        // has told us the order of magnitude, not the number.
        isBand ? 0.55 : 0.85,
        'project-intent:power',
      );
      // Value-level uncertainty for the band, so downstream confidence telemetry
      // sees the width instead of treating the sizing basis as a firm figure.
      // OMITTED for an open-ended bucket — an unbounded band has no meaningful
      // relative width, and Infinity must never be persisted.
      const bb = data.bucketBand;
      if (isBand && bb && !bb.openLow && !bb.openHigh
          && bb.facilityMw.low != null && bb.facilityMw.high != null) {
        const point = bb.sizingBasisMw;
        sed.power.valueUncertainty = [
          {
            field: 'bucketBand.facilityMw',
            pointEstimate: point,
            low: bb.facilityMw.low,
            high: bb.facilityMw.high,
            ciBasis: 'class-estimate',
            relativeWidthPct: point > 0
              ? Math.round(((bb.facilityMw.high - bb.facilityMw.low) / point) * 100)
              : undefined,
          },
        ];
      }
      wrote = true;
    } else {
      // Q2 — a power requirement recorded as unknown is an answer, not an ask.
      const next = emptyOrRecordedUnknownSection<PowerSemanticsEvidence>(
        sed.power,
        isIntentFieldExplicitlyUnknown(intent, 'powerRequirement') ? 'powerRequirement' : null,
        'Project Intent — power requirement recorded as unknown by the user',
        () => emptySection<PowerSemanticsEvidence>('Project Intent — power requirement not yet declared', 'Power'),
      );
      if (next) sed.power = next;
    }
  }

  // ── firm capacity / ELCC (P2-3) ──
  if (!isProtected(sed.firmCapacity)) {
    const isoRegion =
      sed.grid?.status === 'verified' ? sed.grid.data?.isoRegion ?? null : null;
    const data = buildFirmCapacityEvidenceFromIntent(intent, isoRegion);
    if (data) {
      sed.firmCapacity = intentStampedSection(
        data,
        'Project Intent — firm-capacity / ELCC accreditation target',
        0.85,
        'project-intent:firmCapacity',
      );
      wrote = true;
    } else {
      // Q2 — a firm-capacity target recorded as unknown is an answer, not an ask.
      const next = emptyOrRecordedUnknownSection<FirmCapacityEvidence>(
        sed.firmCapacity,
        isIntentFieldExplicitlyUnknown(intent, 'capacityCreditIntent') ? 'capacityCreditIntent' : null,
        'Project Intent — firm-capacity target recorded as unknown by the user',
        () => emptySection<FirmCapacityEvidence>('Project Intent — firm-capacity intent not yet declared', 'Firm capacity / ELCC'),
      );
      if (next) sed.firmCapacity = next;
    }
  }

  return wrote;
}
